import React from 'react';
import { motion } from 'framer-motion';
import './Features.css';

const Features = () => {
  const featureGroups = [
    {
      category: 'LIVE COPILOT',
      tagline: 'Real-time audio transcription and stealth HUD.',
      cards: [
        {
          title: 'Live Copilot',
          description: 'Instant answers and code solutions across 70+ languages with <180ms latency.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
          ),
          badge: 'LIVE NOW',
          isLive: true
        },
        {
          title: 'Stealth Overlay',
          description: 'Hardware overlay invisible to Zoom, Google Meet, Teams, and screen shares.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" x2="23" y1="1" y2="23"/></svg>
          ),
          badge: 'LIVE NOW',
          isLive: true
        },
        {
          title: 'Screen OCR',
          description: 'Instant parsing of LeetCode algorithms, diagrams, and system architecture.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
          ),
          badge: 'LIVE NOW',
          isLive: true
        }
      ]
    },
    {
      category: 'PREPARATION',
      tagline: 'Practice and prepare before your interview.',
      cards: [
        {
          title: 'Resume Analyzer',
          description: 'Match your resume against job specs to identify missing keywords.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="16" x2="8" y1="13"/><line x1="16" x2="8" y1="17"/><line x1="10" x2="8" y1="9"/></svg>
          ),
          badge: 'COMING SOON',
          isLive: false
        },
        {
          title: 'Mock Interviews',
          description: 'Simulate technical and behavioral rounds with adaptive voice follow-ups.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
          ),
          badge: 'COMING SOON',
          isLive: false
        },
        {
          title: 'Resume Builder',
          description: 'Generate tailored bullet points with quantifiable achievements.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
          ),
          badge: 'COMING SOON',
          isLive: false
        }
      ]
    },
    {
      category: 'AUTOMATION',
      tagline: 'Workflows for applications and recruiter outreach.',
      cards: [
        {
          title: 'Auto-Apply',
          description: 'One-click autofill for Greenhouse, Lever, and Workday job portals.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
          ),
          badge: 'COMING SOON',
          isLive: false
        },
        {
          title: 'Recruiter Outreach',
          description: 'High-conversion outbound messages for hiring managers.',
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>
          ),
          badge: 'COMING SOON',
          isLive: false
        }
      ]
    }
  ];

  return (
    <section className="coprep-features-section" id="features">
      <div className="coprep-features-container">
        
        {/* Header */}
        <motion.div 
          className="section-header-centered"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="badge-pill">FEATURES</div>
          <h2 className="section-main-title">
            Platform <span className="gradient-primary">Features</span>
          </h2>
          <p className="section-main-subtitle">
            Live interview copilot today, smart prep and career tools tomorrow.
          </p>
        </motion.div>

        {/* Feature Groups */}
        <div className="feature-categories-stack">
          {featureGroups.map((group, gIdx) => (
            <div key={gIdx} className="feature-category-block">
              
              <div className="category-header-row">
                <div>
                  <span className={`category-kicker ${gIdx === 0 ? 'kicker-live' : 'kicker-soon'}`}>
                    {group.category}
                  </span>
                  <p className="category-tagline">{group.tagline}</p>
                </div>
              </div>

              <div className="category-cards-grid">
                {group.cards.map((card, cIdx) => (
                  <motion.div 
                    key={cIdx}
                    className={`feature-bento-card glass-card ${card.isLive ? 'card-live' : 'card-coming-soon'}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: cIdx * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="card-top-badge-row">
                      <div className={`feature-icon-box ${card.isLive ? 'icon-live' : 'icon-soon'}`}>
                        {card.icon}
                      </div>
                      <span className={`feature-pill-badge ${card.isLive ? 'badge-live' : 'badge-soon'}`}>
                        {card.badge}
                      </span>
                    </div>

                    <h3 className="feature-card-title">{card.title}</h3>
                    <p className="feature-card-desc">{card.description}</p>
                  </motion.div>
                ))}
              </div>

            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Features;
